import type {
  ClearingDayPosition,
  ClearingPosition,
  ParticipantNet,
  Settlement,
  SuggestedTransfer,
} from '@pf/contracts';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/auth-context';
import {
  useClearingPositions,
  useDecideSettlement,
  useInstructSettlement,
  useSettlements,
  useSubmitSettlementSlip,
} from '@/lib/api/ops-queries';
import { days as daysMs, nowMs, toDate, toIso } from '@/lib/clock';
import { formatCalendarDay, formatDateTime } from '@/lib/datetime';
import { formatMoney } from '@/lib/money';
import { TONE_CLASSES, type StateTone } from '@/lib/states';
import { cn } from '@/lib/utils';
import { WebScopeScopedNotice } from '@/components/domain/web-scope-select';
import { windowButtonClasses } from '@/components/layout/range-picker';
import { DataTable, type Column } from '@/components/layout/data-table';
import { PageHeader, Section } from '@/components/layout/page';
import { Button } from '@/components/ui/button';
import { Callout, EmptyState, ErrorState, TableSkeleton } from '@/components/ui/feedback';
import { Input, Label, Pill } from '@/components/ui/primitives';

/**
 * Clearing — Phase 3 (SAD §15): whether cross-website matching is on, who owes whom, and the
 * settlements that discharge those debts.
 *
 * The badge answers the question this page exists for even when the tables are empty: with
 * clearing OFF every website's pool is isolated (SAD §14, Phase 1/2); with it ON matches may
 * cross websites and each one posts an obligation. Positions are the *net* of those obligations
 * per pair; instructing a settlement freezes a position into a payment order, and the row moves
 * from the first table to the second until a CHECKER concludes it.
 *
 * Dual control is role disjointness, mirrored here from the RBAC matrix: the Instruct button
 * renders for MAKER, the Complete/Fail buttons for CHECKER, and no operator can hold both roles
 * — so the person ordering the transfer can never be the person certifying it happened.
 */
export function ClearingPage(): ReactNode {
  const { t } = useTranslation();
  const positions = useClearingPositions();

  const badge =
    positions.data === undefined ? null : (
      <Pill
        className={cn(
          TONE_CLASSES[positions.data.clearing_enabled ? 'attention' : 'good'].badge,
          'rounded-full',
        )}
      >
        {t(
          positions.data.clearing_enabled
            ? 'clearing.status.enabled'
            : 'clearing.status.disabled',
        )}
      </Pill>
    );

  return (
    <>
      <PageHeader
        title={t('clearing.title')}
        description={t('clearing.description')}
        badge={badge}
      />
      {/*
       * A clearing row names *two* participants, so "scoped" cannot mean "rows where this website
       * is the debtor" — that would silently drop every pair where it is owed money, and a net
       * debtor reading only half its ledger is worse than reading the whole pool. The API and the
       * mock backend both filter on "involves" (`ops.ts`): every pair the scoped site is on either
       * side of. That makes a scoped read here consistent with the unscoped one — the site's own
       * net is the same number either way — which is the thing worth saying before the tables,
       * since nothing about a debtor/creditor pair otherwise tells an operator which of the two
       * sides is the one they scoped to.
       */}
      <WebScopeScopedNotice />
      {positions.isLoading ? <TableSkeleton /> : null}
      {positions.isError ? (
        <ErrorState
          error={positions.error}
          onRetry={() => {
            void positions.refetch();
          }}
        />
      ) : null}
      {positions.data !== undefined ? <OverviewSection view={positions.data} /> : null}
      {positions.data !== undefined ? <PositionsSection view={positions.data} /> : null}
      {positions.data !== undefined ? <DailySection rows={positions.data.daily} /> : null}
      <SettlementsSection />
    </>
  );
}

/**
 * The two views a pair-by-pair list cannot give: where each website stands overall, and how few
 * transfers would settle everyone at once.
 */
function OverviewSection(props: {
  readonly view: {
    items: readonly ClearingPosition[];
    participants: readonly ParticipantNet[];
    suggested_transfers: readonly SuggestedTransfer[];
  };
}): ReactNode {
  const { t } = useTranslation();
  if (props.view.participants.length === 0) return null;

  const participantColumns: Column<ParticipantNet>[] = [
    {
      id: 'participant',
      header: t('clearing.overview.website'),
      priority: 'primary',
      cell: (row) => <span className="font-medium">{row.participant}</span>,
    },
    {
      id: 'net',
      header: t('clearing.overview.net'),
      priority: 'primary',
      align: 'right',
      cell: (row) => (
        // Signed on purpose: a net debtor reads negative, which is the one number an operator
        // scans this table for.
        <span
          className={cn(
            'tabular-nums',
            row.net.startsWith('-') ? TONE_CLASSES.bad.text : TONE_CLASSES.good.text,
          )}
        >
          {formatMoney({ value: row.net.replace('-', ''), currency: row.currency }, { withSymbol: true })}
          {row.net.startsWith('-') ? ` ${t('clearing.overview.owes')}` : ''}
        </span>
      ),
    },
    {
      id: 'counterparties',
      header: t('clearing.overview.counterparties'),
      priority: 'secondary',
      align: 'right',
      cell: (row) => row.counterparties,
    },
  ];

  return (
    <Section
      title={t('clearing.overview.title')}
      description={t('clearing.overview.description')}
    >
      <DataTable
        caption={t('clearing.overview.title')}
        columns={participantColumns}
        rows={[...props.view.participants]}
        rowKey={(row) => row.participant}
      />
      {props.view.suggested_transfers.length > 0 ? (
        <Callout
          tone="info"
          title={t('clearing.overview.planTitle', {
            bilateral: props.view.items.length,
            multilateral: props.view.suggested_transfers.length,
          })}
        >
          <ul className="mt-1 space-y-0.5">
            {props.view.suggested_transfers.map((transfer) => (
              <li key={`${transfer.from}->${transfer.to}`} className="tabular-nums">
                {t('clearing.overview.transfer', {
                  from: transfer.from,
                  to: transfer.to,
                  amount: formatMoney(transfer.amount, { withSymbol: true }),
                })}
              </li>
            ))}
          </ul>
        </Callout>
      ) : null}
    </Section>
  );
}

function PositionsSection(props: {
  readonly view: { clearing_enabled: boolean; items: readonly ClearingPosition[] };
}): ReactNode {
  const { t } = useTranslation();
  const { session } = useAuth();
  const instruct = useInstructSettlement();
  // The position being instructed, and the figure that will actually be transferred for it —
  // prefilled with the whole net, because settling all of it is still the normal thing to do.
  const [target, setTarget] = useState<ClearingPosition | null>(null);
  const [amount, setAmount] = useState('');
  // `settlement:instruct` is MAKER's alone on the server (rbac.ts) — plus SUPER, which holds
  // every permission. Deliberately not `can('MAKER')`: that is true for CHECKER too, and a
  // Checker instructing would be answered with 403 INSUFFICIENT_ROLE.
  const canInstruct =
    session?.area === 'operator' && (session.role === 'MAKER' || session.role === 'SUPER');

  const columns: Column<ClearingPosition>[] = [
    {
      id: 'pair',
      header: t('clearing.table.pair'),
      priority: 'primary',
      cell: (row) => (
        <span className="font-medium">
          {t('clearing.table.owes', { debtor: row.debtor, creditor: row.creditor })}
        </span>
      ),
    },
    {
      id: 'net',
      header: t('clearing.table.netAmount'),
      priority: 'primary',
      align: 'right',
      cell: (row) => (
        <span className="tabular-nums">{formatMoney(row.net_amount, { withSymbol: true })}</span>
      ),
    },
    {
      id: 'entries',
      header: t('clearing.table.entries'),
      priority: 'secondary',
      align: 'right',
      cell: (row) => row.entry_count,
    },
    {
      id: 'oldest',
      header: t('clearing.table.oldest'),
      priority: 'detail',
      cell: (row) => formatDateTime(row.oldest_at),
    },
    ...(canInstruct
      ? [
          {
            id: 'actions',
            header: t('clearing.table.actions'),
            priority: 'primary',
            align: 'right',
            cell: (row: ClearingPosition) => (
              <Button
                size="sm"
                variant="outline"
                disabled={instruct.isPending}
                onClick={() => {
                  setTarget(row);
                  setAmount(row.net_amount.value);
                }}
              >
                {t('clearing.actions.instruct')}
              </Button>
            ),
          } satisfies Column<ClearingPosition>,
        ]
      : []),
  ];

  return (
    <Section title={t('clearing.positions.title')} description={t('clearing.positions.description')}>
      {instruct.isError ? (
        <Callout tone="danger" title={t('clearing.actions.instructFailed')}>
          {instruct.error instanceof Error ? instruct.error.message : null}
        </Callout>
      ) : null}
      {target !== null ? (
        <form
          className="mb-4 flex flex-wrap items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            instruct.mutate(
              {
                debtor: target.debtor,
                creditor: target.creditor,
                currency: target.net_amount.currency,
                // Sent only when the operator actually changed it. An untouched field means
                // "settle this position", and the server should net that afresh — echoing the
                // figure this table rendered would instruct a stale number if a posting landed
                // in between, and pay the difference into a residual nobody asked for.
                ...(amount === target.net_amount.value ? {} : { amount }),
              },
              { onSuccess: () => { setTarget(null); } },
            );
          }}
        >
          <span className="flex-1 min-w-64">
            <Label htmlFor="settlement-amount">
              {t('clearing.actions.instructAmount', {
                debtor: target.debtor,
                creditor: target.creditor,
              })}
            </Label>
            <Input
              id="settlement-amount"
              inputMode="decimal"
              value={amount}
              onChange={(event) => { setAmount(event.target.value); }}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {t('clearing.actions.instructAmountHint')}
            </p>
          </span>
          <Button type="submit" size="sm" disabled={!isPositiveAmount(amount) || instruct.isPending}>
            {t('clearing.actions.instructConfirm')}
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => { setTarget(null); }}>
            {t('common.cancel')}
          </Button>
        </form>
      ) : null}
      <DataTable
        caption={t('clearing.positions.title')}
        columns={columns}
        rows={[...props.view.items]}
        rowKey={(row) => `${row.debtor}->${row.creditor}`}
        empty={
          <EmptyState
            title={t('clearing.empty.title')}
            description={t(
              props.view.clearing_enabled
                ? 'clearing.empty.enabledDescription'
                : 'clearing.empty.disabledDescription',
            )}
          />
        }
      />
    </Section>
  );
}

/**
 * Whether what is typed in the amount box is money the server would accept — the same shape its
 * schema takes (`^\d+(\.\d+)?$`), plus "not zero".
 *
 * A local guard on a disabled button, not a validation: the server refuses the same inputs with
 * a 422 either way. It exists so the refusal a typo earns is a greyed-out button rather than a
 * red callout under a table the operator has to re-read.
 */
function isPositiveAmount(value: string): boolean {
  return /^\d+(\.\d+)?$/.test(value) && Number(value) > 0;
}

/**
 * The windows the daily table can be narrowed to. `null` is "every day that still owes anything",
 * and it is the default on purpose.
 *
 * A `7` default was the obvious choice and the wrong one: this table decomposes *live* exposure,
 * so a position whose obligations are all three weeks old would render a full net above an empty
 * daily table — the one reading an operator must never get from this screen, since it looks
 * exactly like a bug in the netting. Showing everything and letting them narrow cannot lie that
 * way, and the list is bounded anyway: a settled day leaves the ledger with its position.
 */
const DAY_WINDOWS = [7, 30, null] as const;

/** One entry of {@link DAY_WINDOWS}. */
type DayWindow = (typeof DAY_WINDOWS)[number];

/**
 * The same exposure as the table above, cut by the day it was posted (newest first).
 *
 * Answers the question a running total cannot: is ฿45,536 one bad afternoon, or three weeks of
 * drift nobody settled? The rows sum to the pair positions above by construction — same ledger
 * rows, same filter, folded twice (`netByDay` in `list-positions.ts`) — so the two tables can be
 * read against each other, which is the only reason it is worth putting both on one page.
 *
 * Hidden entirely when there is nothing owed: the positions table above already says "no
 * exposure" in that case, and a second empty table under it says the same thing less clearly.
 */
function DailySection(props: { readonly rows: readonly ClearingDayPosition[] }): ReactNode {
  const { t } = useTranslation();
  const [dayWindow, setDayWindow] = useState<DayWindow>(null);

  if (props.rows.length === 0) return null;

  // The cutoff is a *day* comparison, not an instant one: the rows' days were cut at midnight
  // Bangkok server-side, so the boundary has to be expressed in the same terms or "7 days" would
  // drop or keep a whole day depending on the hour the operator happened to look. `YYYY-MM-DD`
  // compares lexicographically the way it compares chronologically.
  const cutoff = dayWindow === null ? null : bangkokDayOf(nowMs() - daysMs(dayWindow - 1));
  const rows = cutoff === null ? props.rows : props.rows.filter((row) => row.day >= cutoff);

  const columns: Column<ClearingDayPosition>[] = [
    {
      id: 'day',
      header: t('clearing.daily.day'),
      priority: 'primary',
      cell: (row) => <span className="font-medium">{formatCalendarDay(row.day)}</span>,
    },
    {
      id: 'pair',
      header: t('clearing.table.pair'),
      priority: 'primary',
      cell: (row) => t('clearing.table.owes', { debtor: row.debtor, creditor: row.creditor }),
    },
    {
      id: 'net',
      header: t('clearing.table.netAmount'),
      priority: 'primary',
      align: 'right',
      cell: (row) => (
        <span className="tabular-nums">{formatMoney(row.net_amount, { withSymbol: true })}</span>
      ),
    },
    {
      id: 'entries',
      header: t('clearing.table.entries'),
      priority: 'secondary',
      align: 'right',
      cell: (row) => row.entry_count,
    },
  ];

  return (
    <Section title={t('clearing.daily.title')} description={t('clearing.daily.description')}>
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t('clearing.daily.window')}>
        {DAY_WINDOWS.map((option) => (
          <button
            key={option ?? 'all'}
            type="button"
            aria-pressed={dayWindow === option}
            className={windowButtonClasses(dayWindow === option)}
            onClick={() => {
              setDayWindow(option);
            }}
          >
            {option === null
              ? t('clearing.daily.windowAll')
              : t('clearing.daily.windowDays', { count: option })}
          </button>
        ))}
      </div>
      <DataTable
        caption={t('clearing.daily.title')}
        columns={columns}
        rows={[...rows]}
        rowKey={(row) => `${row.day} ${row.debtor}->${row.creditor}`}
        empty={
          <EmptyState
            title={t('clearing.daily.emptyWindow.title')}
            description={t('clearing.daily.emptyWindow.description')}
          />
        }
      />
    </Section>
  );
}

/**
 * `YYYY-MM-DD` for an instant, in Bangkok — the client half of the server's day boundary.
 *
 * Pinned to the zone rather than read off the browser for the same reason the server pins it:
 * the rows being filtered were bucketed at midnight Bangkok, and a cutoff computed in some other
 * zone would silently include or exclude an edge day. `en-CA` is the locale whose short date
 * *is* `YYYY-MM-DD`, which is what makes the comparison a plain string compare.
 */
function bangkokDayOf(epochMs: number): string {
  return BANGKOK_ISO_DAY.format(toDate(toIso(epochMs)));
}

/** The formatter behind {@link bangkokDayOf}. */
const BANGKOK_ISO_DAY = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  timeZone: 'Asia/Bangkok',
});

/** How each settlement status presents. */
const SETTLEMENT_TONES: Readonly<Record<Settlement['status'], StateTone>> = {
  INSTRUCTED: 'attention',
  IN_TRANSIT: 'flight',
  COMPLETED: 'good',
  FAILED: 'bad',
};

function SettlementsSection(): ReactNode {
  const { t } = useTranslation();
  const { session } = useAuth();
  const settlements = useSettlements();
  const decide = useDecideSettlement();
  const submitSlip = useSubmitSettlementSlip();
  const [slipFor, setSlipFor] = useState<string | null>(null);
  const [qrPayload, setQrPayload] = useState('');
  // `settlement:decide` mirrors instruct: CHECKER alone on the server (rbac.ts), plus SUPER.
  const canDecide =
    session?.area === 'operator' && (session.role === 'CHECKER' || session.role === 'SUPER');

  if (settlements.isLoading) return <TableSkeleton />;
  if (settlements.isError) {
    return (
      <ErrorState
        error={settlements.error}
        onRetry={() => {
          void settlements.refetch();
        }}
      />
    );
  }
  const rows = settlements.data?.items ?? [];

  const columns: Column<Settlement>[] = [
    {
      id: 'pair',
      header: t('clearing.table.pair'),
      priority: 'primary',
      cell: (row) => (
        <span className="font-medium">
          {t('clearing.table.owes', { debtor: row.debtor, creditor: row.creditor })}
        </span>
      ),
    },
    {
      id: 'net',
      header: t('clearing.table.netAmount'),
      priority: 'primary',
      align: 'right',
      cell: (row) => (
        <span className="tabular-nums">{formatMoney(row.net_amount, { withSymbol: true })}</span>
      ),
    },
    {
      id: 'status',
      header: t('clearing.settlements.statusHeader'),
      priority: 'primary',
      cell: (row) => (
        <Pill className={cn(TONE_CLASSES[SETTLEMENT_TONES[row.status]].badge, 'rounded-full')}>
          {t(`clearing.settlements.status.${row.status}`)}
        </Pill>
      ),
    },
    {
      id: 'window',
      header: t('clearing.settlements.window'),
      priority: 'detail',
      cell: (row) => `${formatDateTime(row.window_start)} – ${formatDateTime(row.window_end)}`,
    },
    {
      id: 'created',
      header: t('clearing.settlements.instructedAt'),
      priority: 'secondary',
      cell: (row) => formatDateTime(row.created_at),
    },
    ...(canDecide
      ? [
          {
            id: 'actions',
            header: t('clearing.table.actions'),
            priority: 'primary',
            align: 'right',
            cell: (row: Settlement) =>
              row.status === 'INSTRUCTED' ? (
                <span className="flex justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={submitSlip.isPending}
                    onClick={() => {
                      setSlipFor(row.id);
                      setQrPayload('');
                    }}
                  >
                    {t('clearing.actions.submitSlip')}
                  </Button>
                  <Button
                    size="sm"
                    disabled={decide.isPending}
                    onClick={() => {
                      decide.mutate({ id: row.id, outcome: 'complete' });
                    }}
                  >
                    {t('clearing.actions.complete')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={decide.isPending}
                    onClick={() => {
                      decide.mutate({ id: row.id, outcome: 'fail' });
                    }}
                  >
                    {t('clearing.actions.fail')}
                  </Button>
                </span>
              ) : null,
          } satisfies Column<Settlement>,
        ]
      : []),
  ];

  return (
    <Section
      title={t('clearing.settlements.title')}
      description={t('clearing.settlements.description')}
    >
      {decide.isError ? (
        <Callout tone="danger" title={t('clearing.actions.decideFailed')}>
          {decide.error instanceof Error ? decide.error.message : null}
        </Callout>
      ) : null}
      {submitSlip.isError ? (
        <Callout tone="danger" title={t('clearing.actions.submitSlipFailed')}>
          {submitSlip.error instanceof Error ? submitSlip.error.message : null}
        </Callout>
      ) : null}
      {slipFor !== null ? (
        <form
          className="mb-4 flex flex-wrap items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            submitSlip.mutate(
              { id: slipFor, qrPayload },
              { onSuccess: () => { setSlipFor(null); } },
            );
          }}
        >
          <span className="flex-1 min-w-64">
            <Label htmlFor="settlement-slip">{t('clearing.actions.qrPayload')}</Label>
            <Input
              id="settlement-slip"
              value={qrPayload}
              onChange={(event) => { setQrPayload(event.target.value); }}
              placeholder={t('clearing.actions.qrPayloadHint')}
            />
          </span>
          <Button type="submit" size="sm" disabled={qrPayload.length === 0 || submitSlip.isPending}>
            {t('clearing.actions.submitSlip')}
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => { setSlipFor(null); }}>
            {t('common.cancel')}
          </Button>
        </form>
      ) : null}
      <DataTable
        caption={t('clearing.settlements.title')}
        columns={columns}
        rows={[...rows]}
        rowKey={(row) => row.id}
        empty={
          <EmptyState
            title={t('clearing.settlements.empty.title')}
            description={t('clearing.settlements.empty.description')}
          />
        }
      />
    </Section>
  );
}
